import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Phone, Download, ImageIcon } from "lucide-react";
import PageHeader from "../components/PageHeader";
import MenuCard from "../components/MenuCard";
import Reveal from "../components/Reveal";
import { MENU_IMAGE } from "../data/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORDER = ["Egg Starters", "Bhurji & Fry", "Curry Specials", "Our Specials", "Special Pulav", "Extra"];

export default function Menu() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("All");

  useEffect(() => {
    axios.get(`${API}/menu`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...ORDER], []);
  const filtered = useMemo(() => {
    if (active === "All") return items;
    return items.filter((i) => i.category === active);
  }, [items, active]);

  return (
    <div data-testid="page-menu">
      <PageHeader
        eyebrow="The full menu"
        title="The"
        accent="full menu."
        subtitle="30+ Indian-style egg dishes — exactly what you'll find at the truck. Cooked fresh, served sizzling."
      />

      {/* OFFICIAL MENU POSTER (user-supplied) */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 pb-12">
        <Reveal>
          <div className="rounded-3xl overflow-hidden border border-yellow-brand/40 glow-yellow bg-white">
            <img
              src={MENU_IMAGE}
              alt="Taste of Egg full menu"
              className="w-full h-auto block"
              data-testid="menu-poster"
            />
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-white/55 text-sm">
            <span className="inline-flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-yellow-brand" />
              Our official in-truck menu — prices in AUD
            </span>
            <a
              href={MENU_IMAGE}
              target="_blank"
              rel="noreferrer"
              className="text-yellow-brand font-bold inline-flex items-center gap-1 hover:underline"
              data-testid="menu-download"
            >
              <Download className="w-4 h-4" /> View full size
            </a>
          </div>
        </Reveal>
      </section>

      {/* Category filter pills */}
      <div className="sticky top-20 z-30 backdrop-blur-xl bg-black/70 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 min-w-max">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`menu-filter-${c.toLowerCase().replace(/[\s&]+/g, "-").replace(/-+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all ${
                  active === c
                    ? "bg-yellow-brand text-black shadow-[0_8px_30px_-10px_rgba(244,187,41,0.7)]"
                    : "bg-white/5 text-white/70 hover:text-yellow-brand"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Searchable text-based menu */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {ORDER.map((cat) => {
          const list = filtered.filter((i) => i.category === cat);
          if (active !== "All" && active !== cat) return null;
          if (!list.length) return null;
          return (
            <div key={cat} className="mb-16">
              <Reveal>
                <h2 className="text-3xl md:text-5xl font-extrabold mb-2">
                  {cat}
                </h2>
                <div className="w-16 h-1 bg-yellow-brand rounded-full" />
              </Reveal>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {list.map((item, i) => (
                  <Reveal key={item.id} delay={i * 0.04}>
                    <MenuCard item={item} />
                  </Reveal>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 pb-20">
        <div className="rounded-3xl bg-yellow-brand text-black p-10 md:p-14 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold balanced">
            Ready to eat?{" "}
            <span className="font-script text-5xl md:text-7xl">Call ahead.</span>
          </h2>
          <a href="tel:0434508712" className="mt-6 inline-flex items-center gap-2 bg-black text-yellow-brand font-extrabold rounded-full px-7 py-3 hover:bg-black/85 transition-colors" data-testid="menu-call-cta">
            <Phone className="w-4 h-4" /> 0434 508 712
          </a>
        </div>
      </section>
    </div>
  );
}
